import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit3,
  Eye,
  Sliders,
  Send,
  Save,
  CheckCircle2,
  Loader2,
  AlertCircle,
  X,
  Clock,
  MoreHorizontal,
  Share2,
  ExternalLink,
  Globe
} from 'lucide-react';
import { useStorefrontData } from '../../context/StorefrontDataContext';

export type LiveSaveStatus = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error';

export interface UnifiedLiveTopBarProps {
  pageTitle: string;
  isAdmin?: boolean;
  isEditMode: boolean;
  isPreviewMode?: boolean;
  onToggleMode: (mode: 'edit' | 'preview') => void;
  saveStatus: LiveSaveStatus;
  lastSavedTime?: string | null;
  hasUnpublishedChanges?: boolean;
  onManualSave?: () => void;
  isSaving?: boolean;
  onOpenDrawer?: () => void;
  onPublish: () => void;
  isPublishing?: boolean;
  publishLabel?: string;
  onClose?: () => void;
  isScheduled?: boolean;
  scheduledAt?: string | null;
  onOpenScheduleModal?: () => void;
  onOpenShareModal?: () => void;
  adminReturnUrl?: string;
  viewSource?: 'published' | 'draft';
  onToggleViewSource?: (source: 'published' | 'draft') => void;
}

export default function UnifiedLiveTopBar({
  pageTitle,
  isAdmin = false,
  isEditMode,
  isPreviewMode = false,
  onToggleMode,
  saveStatus,
  lastSavedTime,
  hasUnpublishedChanges = false,
  onManualSave,
  isSaving = false,
  onOpenDrawer,
  onPublish,
  isPublishing = false,
  publishLabel = 'Publish ke Publik',
  onClose,
  isScheduled = false,
  scheduledAt = null,
  onOpenScheduleModal,
  onOpenShareModal,
  adminReturnUrl = '/admin/cms',
  viewSource = 'published',
  onToggleViewSource
}: UnifiedLiveTopBarProps) {
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const storefront = useStorefrontData();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMoreMenu(false);
      }
    }
    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreMenu]);

  // Global Shortcut: Cmd+S (Mac) / Ctrl+S (Windows/Linux) to save draft
  useEffect(() => {
    if (!isEditMode || !onManualSave) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        e.stopImmediatePropagation();
        onManualSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isEditMode, onManualSave]);

  // Clean, concise publish label
  const formattedPublishLabel = publishLabel
    .replace('ke Publik', '')
    .replace('Sekarang', '')
    .trim();

  // Floating controls when live editor is inactive (closed)
  if (!isEditMode && !isPreviewMode) {
    if (!isAdmin) return null;
    return (
      <>
        {/* Top Warning Banner when admin explicitly activates draft preview while editor is closed */}
        {viewSource === 'draft' && hasUnpublishedChanges && (
          <aside aria-label="Notifikasi Pratinjau Draf" className="fixed top-3 left-1/2 -translate-x-1/2 z-50 animate-fade-in pointer-events-auto">
            <div className="flex items-center gap-2.5 bg-amber-950/95 text-amber-200 border border-amber-500/40 px-4 py-1.5 rounded-full text-xs font-sans shadow-xl backdrop-blur-md">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span>Melihat <strong>Pratinjau Draf</strong> (Pengunjung melihat Versi Publik).</span>
              {onToggleViewSource && (
                <button
                  type="button"
                  onClick={() => onToggleViewSource('published')}
                  className="underline hover:text-white ml-1 font-semibold cursor-pointer"
                >
                  Lihat Versi Publik
                </button>
              )}
            </div>
          </aside>
        )}

        {/* Floating Quick Access Pill in Bottom Right Corner */}
        <aside aria-label="Akses Cepat Editor Admin" className="fixed bottom-6 right-6 z-50 animate-fade-in pointer-events-auto">
          <div className="flex items-center gap-2 bg-[#1C1817]/95 hover:bg-black text-white p-2 pl-3.5 rounded-full shadow-2xl backdrop-blur-md border border-white/20 transition-all">
            {/* Status Indicator */}
            <div className="flex items-center gap-2 shrink-0">
              {hasUnpublishedChanges ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                  <span className="text-xs font-serif font-bold text-amber-300 tracking-wide">Draf Belum Live</span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                  <span className="text-xs font-serif font-bold text-emerald-300 tracking-wide">Live Publik</span>
                </>
              )}
            </div>

            {/* Option B: Segmented View Switcher (Only visible when draft has unpublished changes) */}
            {hasUnpublishedChanges && onToggleViewSource && (
              <div
                className="flex items-center bg-black/60 border border-white/15 rounded-full p-0.5 ml-1"
                title="Pilih versi tampilan halaman saat editor ditutup"
              >
                <button
                  type="button"
                  onClick={() => onToggleViewSource('published')}
                  className={`px-2.5 py-1 text-[11px] font-sans rounded-full transition-all cursor-pointer ${
                    viewSource !== 'draft'
                      ? 'bg-white/20 text-white font-bold shadow-sm'
                      : 'text-white/60 hover:text-white'
                  }`}
                  title="Tampilkan versi publik asli yang dilihat pengunjung"
                >
                  🌐 Publik
                </button>
                <button
                  type="button"
                  onClick={() => onToggleViewSource('draft')}
                  className={`px-2.5 py-1 text-[11px] font-sans rounded-full transition-all cursor-pointer ${
                    viewSource === 'draft'
                      ? 'bg-amber-500/30 text-amber-200 border border-amber-500/50 font-bold shadow-sm'
                      : 'text-white/60 hover:text-amber-200'
                  }`}
                  title="Tampilkan pratinjau draf tersimpan yang belum terbit"
                >
                  📝 Draf
                </button>
              </div>
            )}

            {/* Live Edit Button */}
            <button
              type="button"
              onClick={() => onToggleMode('edit')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-wine hover:bg-brand-wine/90 text-white text-xs font-bold rounded-full shadow transition-all cursor-pointer ml-1"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>{hasUnpublishedChanges ? `Lanjut Edit ${pageTitle}` : `Live Edit ${pageTitle}`}</span>
            </button>

            <Link
              to={adminReturnUrl}
              className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
              title="Buka Dashboard Admin"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
        </aside>
      </>
    );
  }

  return (
    <header className="fixed top-3 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-1rem)] max-w-4xl pointer-events-none transition-all flex justify-center">
      <div className="pointer-events-auto bg-[#1C1817]/95 sm:bg-[#221A1A]/95 backdrop-blur-xl border border-white/15 shadow-[0_16px_40px_-8px_rgba(0,0,0,0.6)] text-white px-2 sm:px-4 py-1.5 rounded-full flex items-center justify-between gap-1.5 sm:gap-4 transition-all w-full max-w-fit sm:w-full">
        
        {/* Left: Admin Hub Link & Page Title */}
        <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0 min-w-0">
          <Link
            to={adminReturnUrl}
            className="p-1 sm:p-0 flex items-center gap-1 text-xs text-white/70 hover:text-white transition-colors cursor-pointer rounded-full hover:bg-white/10 sm:hover:bg-transparent"
            title="Kembali ke Dashboard Admin"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline font-mono text-[11px] uppercase tracking-wider">Hub</span>
          </Link>

          <span className="text-white/20 hidden sm:inline">•</span>

          <span className="text-xs font-serif font-bold text-white truncate max-w-[140px] hidden sm:inline">
            {pageTitle}
          </span>
        </div>

        {/* Center: Minimalist Mode Switcher & Compact Save Status */}
        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          {/* Segmented Switcher Pill */}
          <div className="flex items-center bg-black/50 border border-white/15 rounded-full p-0.5 shadow-inner">
            <button
              type="button"
              onClick={() => onToggleMode('edit')}
              className={`flex items-center justify-center gap-1.5 px-2.5 sm:px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer ${
                isEditMode && !isPreviewMode
                  ? 'bg-brand-wine text-white shadow-md shadow-brand-wine/50 font-black'
                  : 'text-white/70 hover:text-white'
              }`}
              title="Mode Edit Langsung"
            >
              <Edit3 className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">Edit</span>
            </button>
            <button
              type="button"
              onClick={() => onToggleMode('preview')}
              className={`flex items-center justify-center gap-1.5 px-2.5 sm:px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer ${
                isPreviewMode || !isEditMode
                  ? 'bg-white text-gray-900 shadow-md font-black'
                  : 'text-white/70 hover:text-white'
              }`}
              title="Lihat Tampilan Pengunjung"
            >
              <Eye className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">Preview</span>
            </button>
          </div>

          {/* Compact Auto-Save Indicator */}
          {isEditMode && (
            <div className="flex items-center shrink-0">
              {saveStatus === 'saving' || isSaving ? (
                <span
                  className="flex items-center gap-1 text-amber-300 text-xs"
                  title="Sedang menyimpan perubahan..."
                >
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                </span>
              ) : saveStatus === 'saved' ? (
                <span
                  className="flex items-center gap-1 text-emerald-400 text-xs"
                  title={`Draf tersimpan ${lastSavedTime ? `(${lastSavedTime})` : ''}`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span className="hidden md:inline font-mono text-[10px] text-emerald-300/80">
                    Tersimpan
                  </span>
                </span>
              ) : saveStatus === 'unsaved' ? (
                <span
                  className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"
                  title="Ada perubahan belum tersimpan"
                />
              ) : saveStatus === 'error' ? (
                <span
                  className="flex items-center text-rose-400 text-xs"
                  title="Gagal menyimpan otomatis"
                >
                  <AlertCircle className="w-3.5 h-3.5" />
                </span>
              ) : null}
            </div>
          )}
        </div>

        {/* Right: Primary Action (Publish) & Consolidated More Menu (•••) */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Primary Action Button (Publish / Terbitkan) */}
          <button
            type="button"
            onClick={onPublish}
            disabled={isPublishing}
            className="inline-flex items-center justify-center gap-1.5 h-7.5 px-3 sm:px-4 bg-brand-wine hover:bg-brand-wine-deep text-white text-xs font-bold rounded-full shadow-lg shadow-brand-wine/30 transition-all cursor-pointer disabled:opacity-50"
            title={formattedPublishLabel || 'Publish perubahan'}
          >
            {isPublishing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5 shrink-0" />
            )}
            <span className="hidden sm:inline">{formattedPublishLabel || 'Publish'}</span>
          </button>

          {/* Consolidated Secondary Actions Menu (•••) */}
          <div className="relative" ref={moreMenuRef}>
            <button
              type="button"
              onClick={() => setShowMoreMenu((prev) => !prev)}
              className={`p-1.5 h-7.5 w-7.5 rounded-full border transition-all cursor-pointer flex items-center justify-center ${
                showMoreMenu
                  ? 'bg-white text-gray-900 border-white'
                  : 'bg-white/10 hover:bg-white/20 text-white border-white/15'
              }`}
              title="Menu Pengaturan & Opsi Tambahan"
            >
              <MoreHorizontal className="w-3.5 h-3.5" />
            </button>

            {/* Dropdown Menu Popover */}
            {showMoreMenu && (
              <div className="absolute top-full right-0 mt-2 w-56 bg-[#1C1817]/98 backdrop-blur-2xl border border-white/15 rounded-2xl p-1.5 shadow-2xl text-xs space-y-0.5 z-50 animate-in fade-in zoom-in-95 text-white">
                {/* Drawer Settings */}
                {onOpenDrawer && isEditMode && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreMenu(false);
                      onOpenDrawer();
                    }}
                    className="w-full text-left px-3 py-2 rounded-xl hover:bg-white/10 text-white flex items-center gap-2.5 transition-colors cursor-pointer"
                  >
                    <Sliders className="w-3.5 h-3.5 text-white/70" />
                    <span>Pengaturan & SEO</span>
                  </button>
                )}

                {/* Master Global Layout & Footer */}
                {isEditMode && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreMenu(false);
                      storefront.openMasterDrawer('footer');
                    }}
                    className="w-full text-left px-3 py-2 rounded-xl hover:bg-amber-500/20 text-amber-200 flex items-center gap-2.5 transition-colors cursor-pointer"
                  >
                    <Globe className="w-3.5 h-3.5 text-amber-300" />
                    <span>Master Footer & Layout</span>
                  </button>
                )}

                {/* Manual Save */}
                {onManualSave && isEditMode && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreMenu(false);
                      onManualSave();
                    }}
                    className="w-full text-left px-3 py-2 rounded-xl hover:bg-white/10 text-white flex items-center justify-between transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-2.5">
                      <Save className="w-3.5 h-3.5 text-white/70" />
                      <span>Simpan Draf</span>
                    </div>
                    <span className="text-[10px] text-white/40 font-mono">⌘S</span>
                  </button>
                )}

                {/* Schedule Modal */}
                {onOpenScheduleModal && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreMenu(false);
                      onOpenScheduleModal();
                    }}
                    className="w-full text-left px-3 py-2 rounded-xl hover:bg-white/10 text-white flex items-center gap-2.5 transition-colors cursor-pointer"
                  >
                    <Clock className="w-3.5 h-3.5 text-amber-400" />
                    <span className="truncate">
                      {isScheduled && scheduledAt
                        ? `Jadwal: ${new Date(scheduledAt).toLocaleDateString('id-ID', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}`
                        : 'Atur Jadwal Terbit'}
                    </span>
                  </button>
                )}

                {/* Share Link */}
                {onOpenShareModal && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreMenu(false);
                      onOpenShareModal();
                    }}
                    className="w-full text-left px-3 py-2 rounded-xl hover:bg-white/10 text-white flex items-center gap-2.5 transition-colors cursor-pointer"
                  >
                    <Share2 className="w-3.5 h-3.5 text-white/70" />
                    <span>Bagikan Preview</span>
                  </button>
                )}

                {/* Status indicator note in menu */}
                {hasUnpublishedChanges !== undefined && (
                  <div className="px-3 py-1.5 text-[10px] font-mono text-white/40 border-t border-white/10 my-1">
                    Status:{' '}
                    <span className={hasUnpublishedChanges ? 'text-amber-300' : 'text-emerald-400'}>
                      {hasUnpublishedChanges ? 'Draf Belum Live' : 'Live Publik'}
                    </span>
                  </div>
                )}

                {/* Close Live Editor or Return to Admin */}
                <div className="border-t border-white/10 pt-1 mt-1">
                  {onClose && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowMoreMenu(false);
                        onClose();
                      }}
                      className="w-full text-left px-3 py-2 rounded-xl hover:bg-rose-500/20 text-rose-300 flex items-center gap-2.5 transition-colors cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                      <span>Tutup Live Editor</span>
                    </button>
                  )}
                  <Link
                    to={adminReturnUrl}
                    className="w-full text-left px-3 py-2 rounded-xl hover:bg-white/10 text-white/60 hover:text-white flex items-center gap-2.5 transition-colors"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span>Kembali ke Admin CMS</span>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
